import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  X,
  Truck,
  MapPin,
  User,
  Car,
  Users,
  Calendar,
  Package,
  ChevronDown,
  Search,
  ArrowRight,
  Info,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  AlertTriangle,
  UserCheck,
  RotateCcw,
  Clock,
  ClipboardList,
} from "lucide-react";
import { domain, domainAccounting } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";
import { TripTicketNumberSeriesSelect } from "../Selections";

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const formatCurrency = (val) =>
  new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP" }).format(
    val || 0,
  );

const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const getFullName = (p) =>
  [p?.firstName, p?.lastName].filter(Boolean).join(" ") || "—";

const getInitials = (p) =>
  [p?.firstName?.[0] || "", p?.lastName?.[0] || ""].join("").toUpperCase();

const inputCls =
  "block w-full px-3 py-2.5 border border-gray-300 rounded-xl text-sm bg-white outline-none transition focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400";

const labelCls =
  "block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide";

// ─────────────────────────────────────────────────────────────
// STEP INDICATOR
// ─────────────────────────────────────────────────────────────
const StepIndicator = ({ step }) => {
  const steps = [
    { n: 1, label: "Select Tasks" },
    { n: 2, label: "Shipment Info" },
    { n: 3, label: "Review" },
  ];
  return (
    <div className="flex items-center">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                step === s.n
                  ? "bg-indigo-600 text-white shadow-sm shadow-indigo-200"
                  : step > s.n
                    ? "bg-green-500 text-white"
                    : "bg-gray-100 text-gray-400"
              }`}
            >
              {step > s.n ? <CheckCircle size={14} /> : s.n}
            </div>
            <span
              className={`text-xs font-semibold hidden sm:block ${
                step === s.n
                  ? "text-indigo-600"
                  : step > s.n
                    ? "text-green-600"
                    : "text-gray-400"
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-3 ${
                step > s.n ? "bg-green-300" : "bg-gray-200"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// TASK TABLE ROW
// ─────────────────────────────────────────────────────────────
const TaskRow = ({ task, isSelected, onToggle, isPreSelected }) =>  { 
  return (
    <>
      <tr
        onClick={() => onToggle(task.uniqueId)}
        className={`cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${
          isSelected ? "bg-indigo-50/80" : "hover:bg-gray-50"
        }`}
      >
        <td className="px-4 py-3 w-10">
          <div
            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
              isSelected ? "bg-indigo-600 border-indigo-600" : "border-gray-300"
            }`}
          >
            {isSelected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path
                  d="M1 4L3.5 6.5L9 1"
                  stroke="white"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {task.taskType === "DO" ? (
              <Package size={14} className="text-blue-500" />
            ) : (
              <RotateCcw size={14} className="text-green-500" />
            )}
            <p
              className={`text-sm font-bold ${
                isSelected ? "text-indigo-800" : "text-gray-800"
              }`}
            >
              {task.documentNumber}
            </p>
            {isPreSelected && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-100 text-indigo-600 border border-indigo-200 uppercase tracking-wide">
                Pre-selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 mt-1">
            <span
              className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${
                task.taskType === "DO"
                  ? "bg-blue-50 text-blue-600"
                  : "bg-green-50 text-green-600"
              }`}
            >
              {task.taskType === "DO" ? "Outbound Delivery" : "Inbound Collection"}
            </span>
            {/* {task.salesOrderNumber && (
              <span className="text-[10px] text-gray-400">
                (SO: {task.salesOrderNumber})
              </span>
            )} */}
          </div>
        </td>
        <td className="px-4 py-3">
          <p className="text-sm font-medium text-gray-700">{task.customerName}</p>
          
          {/* Max height 40px with a thin scrollbar */}
          <div className="flex flex-col gap-1 max-h-[40px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-200 pr-2">
            {task.salesOrderNumbers?.map((item, index) => (
              <span key={index} className="text-[10px] text-gray-400 whitespace-nowrap">
                {`${item.salesOrderNumber} | ${item.salesmanName ?? "No Salesman"} | ${item.locationName ?? "Unknown"}`}
              </span>
            ))}
          </div>
        </td>
        <td className="px-4 py-3 text-right">
          <span
            className={`text-sm font-bold ${
              isSelected ? "text-indigo-700" : "text-gray-700"
            }`}
          >
            {formatCurrency(task.totalAmount)}
          </span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
            <Package size={9} /> Pending Dispatch
          </span>
        </td>
      </tr>
    </>
)};

// ─────────────────────────────────────────────────────────────
// SELECTED TASK PILL
// ─────────────────────────────────────────────────────────────
const SelectedTaskPill = ({ taskItem, onRemove, isPreSelected }) => (
  <div
    className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg ${
      isPreSelected
        ? "bg-indigo-100 border-indigo-300"
        : taskItem.taskType === "DO"
          ? "bg-blue-50 border-blue-200"
          : "bg-green-50 border-green-200"
    }`}
  >
    <div className="flex flex-col">
      <span
        className={`text-xs font-bold ${
          taskItem.taskType === "DO" ? "text-blue-800" : "text-green-800"
        }`}
      >
        {taskItem.documentNumber}
      </span>
      <span
        className={`text-[10px] truncate max-w-[120px] ${
          taskItem.taskType === "DO" ? "text-blue-600" : "text-green-600"
        }`}
      >
        {taskItem.customerName}
      </span>
    </div>
    <button
      onClick={(e) => {
        e.stopPropagation();
        onRemove(taskItem.uniqueId);
      }}
      className="ml-1 text-gray-400 hover:text-red-500 transition"
    >
      <X size={12} />
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────
// MINI PAGINATION
// ─────────────────────────────────────────────────────────────
const MiniPagination = ({ currentPage, totalPages, onChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 py-1">
      <button
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <ChevronLeft size={14} />
      </button>
      <span className="text-xs text-gray-500 font-medium">
        {currentPage} / {totalPages}
      </span>
      <button
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// VEHICLE SELECTION MODAL
// ─────────────────────────────────────────────────────────────
const VehicleSelectionModal = ({
  isOpen,
  onClose,
  vehicles,
  selectedId,
  onConfirm,
}) => {
  const [search, setSearch] = useState("");
  const [localId, setLocalId] = useState(selectedId);
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setLocalId(selectedId);
      setSearch("");
      setPage(1);
    }
  }, [isOpen, selectedId]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return vehicles.filter(
      (v) =>
        (v.vehicleName || "").toLowerCase().includes(q) ||
        (v.vehicleType || "").toLowerCase().includes(q) ||
        (v.plateNumber || "").toLowerCase().includes(q),
    );
  }, [vehicles, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const selectedVehicle = vehicles.find((v) => v.id === localId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex h-full max-h-[80vh] w-full max-w-lg flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="shrink-0 border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-xl text-white">
                <Car size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Select Vehicle
                </h2>
                <p className="text-xs text-gray-500">
                  {vehicles.length} vehicles available
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, type, or plate number…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Car size={22} className="text-gray-400" />
              </div>
              <p className="text-gray-600 font-semibold text-sm">
                No vehicles found
              </p>
              <p className="text-gray-400 text-xs mt-1">
                {search
                  ? "Try a different search term."
                  : "No vehicles available."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 w-10" />
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Vehicle Details
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide text-center">
                    Type
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((v) => {
                  const isSel = localId === v.id;
                  return (
                    <tr
                      key={v.id}
                      onClick={() => setLocalId(v.id)}
                      className={`cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${
                        isSel ? "bg-indigo-50/80" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3">
                        <div
                          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                            isSel ? "border-indigo-600" : "border-gray-300"
                          }`}
                        >
                          {isSel && (
                            <div className="w-2.5 h-2.5 rounded-full bg-indigo-600" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {v.plateNumber && (
                          <p className="text-lg font-bold text-gray-500 mt-0.5 flex items-center gap-1 font-mono">
                            <CreditCard size={9} className="text-gray-400" />{" "}
                            {v.plateNumber}
                          </p>
                        )}
                        <p
                          className={`text-sm ${
                            isSel ? "text-indigo-800" : "text-gray-800"
                          }`}
                        >
                          {v.vehicleName}
                        </p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {v.vehicleType ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-600 border border-indigo-100">
                            {v.vehicleType}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > 0 && (
          <div className="shrink-0 px-6 py-2 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                Showing {filtered.length} of {vehicles.length} vehicles
              </p>
              <MiniPagination
                currentPage={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            </div>
          </div>
        )}

        <div className="shrink-0 border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {selectedVehicle ? (
              <span className="font-semibold text-indigo-600">
                ✓ {selectedVehicle.vehicleName}
              </span>
            ) : (
              <span className="text-gray-400">No vehicle selected</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(localId);
                onClose();
              }}
              disabled={!localId}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <CheckCircle size={15} /> Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// PERSONNEL SELECTION MODAL
// ─────────────────────────────────────────────────────────────
const PersonnelSelectionModal = ({
  isOpen,
  onClose,
  title,
  icon: Icon = Users,
  personnel,
  selectedIds,
  onConfirm,
  isOptional = false, // <--- ADDED PROPS TO HANDLE OPTIONAL SELECTIONS
}) => {
  const [search, setSearch] = useState("");
  const [localIds, setLocalIds] = useState([...selectedIds]);
  const PAGE_SIZE = 8;
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setLocalIds([...selectedIds]);
      setSearch("");
      setPage(1);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return personnel.filter(
      (p) =>
        getFullName(p).toLowerCase().includes(q) ||
        (p.employeeId || "").toLowerCase().includes(q),
    );
  }, [personnel, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggle = (id) =>
    setLocalIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const allPageSelected =
    paged.length > 0 && paged.every((p) => localIds.includes(p.id));

  const togglePage = () => {
    if (allPageSelected) {
      const pageIds = new Set(paged.map((p) => p.id));
      setLocalIds((prev) => prev.filter((id) => !pageIds.has(id)));
    } else {
      const toAdd = paged
        .map((p) => p.id)
        .filter((id) => !localIds.includes(id));
      setLocalIds((prev) => [...prev, ...toAdd]);
    }
  };

  const selectedItems = personnel.filter((p) => localIds.includes(p.id));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm">
      <div className="relative flex h-full max-h-[88vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="shrink-0 border-b bg-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-600 rounded-xl text-white">
                <Icon size={18} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-gray-900">{title}</h2>
                <p className="text-xs text-gray-500">
                  {personnel.length} record{personnel.length !== 1 ? "s" : ""}{" "}
                  available
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {localIds.length > 0 && (
          <div className="shrink-0 px-6 py-3 bg-indigo-50 border-b border-indigo-100">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">
                {localIds.length} selected
              </span>
              <button
                onClick={() => setLocalIds([])}
                className="text-xs text-red-500 hover:text-red-700 font-semibold transition"
              >
                Clear all
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedItems.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-1.5 bg-indigo-50 border border-indigo-200 rounded-lg"
                >
                  <div className="w-5 h-5 rounded-full bg-indigo-600 text-white flex items-center justify-center text-[9px] font-bold shrink-0">
                    {getInitials(p)}
                  </div>
                  <span className="text-xs font-bold text-indigo-800 max-w-[120px] truncate">
                    {getFullName(p)}
                  </span>
                  <button
                    onClick={() => toggle(p.id)}
                    className="ml-1 text-indigo-400 hover:text-red-500 transition"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="shrink-0 px-6 py-3 border-b border-gray-100 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or employee ID…"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                <Users size={22} className="text-gray-400" />
              </div>
              <p className="text-gray-600 font-semibold text-sm">
                No personnel found
              </p>
              <p className="text-gray-400 text-xs mt-1">
                {search
                  ? "Try a different search term."
                  : "No records available."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <div
                      onClick={togglePage}
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                        allPageSelected
                          ? "bg-indigo-600 border-indigo-600"
                          : "border-gray-300 hover:border-indigo-400"
                      }`}
                    >
                      {allPageSelected && (
                        <svg
                          width="10"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M1 4L3.5 6.5L9 1"
                            stroke="white"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Name
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide hidden sm:table-cell">
                    EMP ID
                  </th>
                  <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide text-center hidden sm:table-cell">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => {
                  const isSel = localIds.includes(p.id);
                  return (
                    <tr
                      key={p.id}
                      onClick={() => toggle(p.id)}
                      className={`cursor-pointer transition-colors border-b border-gray-50 last:border-0 ${
                        isSel ? "bg-indigo-50/80" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="px-4 py-3 w-10">
                        <div
                          className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                            isSel
                              ? "bg-indigo-600 border-indigo-600"
                              : "border-gray-300"
                          }`}
                        >
                          {isSel && (
                            <svg
                              width="10"
                              height="8"
                              viewBox="0 0 10 8"
                              fill="none"
                            >
                              <path
                                d="M1 4L3.5 6.5L9 1"
                                stroke="white"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                              isSel
                                ? "bg-indigo-600 text-white"
                                : "bg-gray-100 text-gray-600"
                            }`}
                          >
                            {getInitials(p)}
                          </div>
                          <div>
                            <p
                              className={`text-sm font-bold ${
                                isSel ? "text-indigo-800" : "text-gray-800"
                              }`}
                            >
                              {getFullName(p)}
                            </p>
                            {p.licenseNumber && (
                              <p className="text-[10px] text-gray-400 flex items-center gap-1">
                                <CreditCard size={9} /> {p.licenseNumber}
                              </p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden sm:table-cell">
                        <span className="font-mono text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {p.employeeId || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center hidden sm:table-cell">
                        <div className="flex flex-col items-center gap-0.5">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              p.status === "Active"
                                ? "bg-green-50 text-green-700 border-green-200"
                                : p.status === "On Leave"
                                  ? "bg-amber-50 text-amber-700 border-amber-200"
                                  : "bg-gray-100 text-gray-500 border-gray-200"
                            }`}
                          >
                            {p.status || "—"}
                          </span>
                          {p.licenseExpiryDate &&
                            (() => {
                              const days =
                                (new Date(p.licenseExpiryDate) - new Date()) /
                                86400000;
                              if (days < 0)
                                return (
                                  <span className="inline-flex items-center gap-0.5 text-[9px] text-red-500 font-semibold">
                                    <AlertTriangle size={8} /> Expired
                                  </span>
                                );
                              if (days <= 30)
                                return (
                                  <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-500 font-semibold">
                                    <AlertTriangle size={8} /> Expiring soon
                                  </span>
                                );
                              return null;
                            })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {filtered.length > 0 && (
          <div className="shrink-0 px-6 py-2 bg-gray-50 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                Showing {filtered.length} of {personnel.length} available
              </p>
              <MiniPagination
                currentPage={page}
                totalPages={totalPages}
                onChange={setPage}
              />
            </div>
          </div>
        )}

        <div className="shrink-0 border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <div className="text-xs text-gray-500">
            {localIds.length > 0 ? (
              <span className="font-semibold text-indigo-600">
                {localIds.length} person{localIds.length !== 1 ? "nel" : ""}{" "}
                selected
              </span>
            ) : isOptional ? (
              // <--- If it's optional, tell the user it's okay to have none selected
              <span className="text-gray-400">None selected (Optional)</span>
            ) : (
              <span className="text-gray-400">Select at least one</span>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onConfirm(localIds);
                onClose();
              }}
              // <--- Only disable if it's NOT optional AND length is 0
              disabled={!isOptional && localIds.length === 0}
              className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              <UserCheck size={15} />
              Confirm {localIds.length > 0 ? `(${localIds.length})` : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────
const CreateShipmentModal = ({ onClose, onSuccess, initialDO = null }) => {
  const currentUserId = useSelector(selectUserID);

  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);

  // Map initial DO (if any) to generic task format
  const initTask = initialDO
    ? {
        ...initialDO,
        taskType: "DO",
        uniqueId: `DO-${initialDO.id}`,
        documentNumber: initialDO.deliveryOrderNumber, // Raw mapping for initial inject
      }
    : null;

  const [availableTasks, setAvailableTasks] = useState(
    initTask ? [initTask] : [],
  );
  const [selectedTaskIds, setSelectedTaskIds] = useState(
    initTask ? [initTask.uniqueId] : [],
  );
  const [taskSearch, setTaskSearch] = useState("");
  const [locationFilter, setLocationFilter] = useState("All");

  // ── Reference data ──
  const [vehicles, setVehicles] = useState([]);
  const [allPersonnel, setAllPersonnel] = useState([]);

  const getLocalDateTime = () => {
    const now = new Date();
    // This adjusts the time by subtracting the timezone offset
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  // ── Form Formats perfectly to C# CreateShipmentDto ──
  const [form, setForm] = useState({
    vehicleId: null,
    shippingLocation: "", // String free-text
    driverIds: [], // Maps exactly to List<int> DriverIds
    personnelIds: [], // Maps exactly to List<int> PersonnelIds
    plannedDate: getLocalDateTime(),
    notes: "",
    selectedItems: []
  });

  const [tripTicketNumberSeries, setTripTicketNumberSeries] = useState(null);

  // ── Selection modal toggles ──
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [driverModalOpen, setDriverModalOpen] = useState(false);
  const [personnelModalOpen, setPersonnelModalOpen] = useState(false);

  // ── Fetch all reference data in parallel ──
  useEffect(() => {
    const load = async () => {
      setIsFetching(true);
      try {
        const [dosRes, trsRes, vehiclesRes, personnelRes] = await Promise.all([
          axios.get(`${domain}/api/Shipments/available-dos`),
          axios.get(`${domain}/api/TradeReturns/available-returns`),
          axios.get(`${domain}/api/Vehicles`),
          axios.get(`${domain}/api/LogisticsPersonnel`),
        ]);

        const fetchedDos = Array.isArray(dosRes.data)
          ? dosRes.data
          : (dosRes.data?.data ?? []);
        const fetchedTrs = Array.isArray(trsRes.data)
          ? trsRes.data
          : (trsRes.data?.data ?? []);

        // Flatten tasks
        const tasks = [
          ...fetchedDos.map((d) => ({
            ...d,
            taskType: "DO",
            uniqueId: `DO-${d.id}`,
          })),
          ...fetchedTrs.map((t) => ({
            ...t,
            taskType: "TR",
            uniqueId: `TR-${t.id}`,
          })),
        ];

        if (initTask) {
          const alreadyIn = tasks.some((t) => t.uniqueId === initTask.uniqueId);
          setAvailableTasks(alreadyIn ? tasks : [initTask, ...tasks]);
        } else {
          setAvailableTasks(tasks);
        }

        setVehicles(Array.isArray(vehiclesRes.data) ? vehiclesRes.data : []);
        setAllPersonnel(
          Array.isArray(personnelRes.data) ? personnelRes.data : [],
        );
      } catch (error) {
        console.error('There was a problem adding data:', error);
        toast.error("Failed to load dispatch data.");
        if (initTask) setAvailableTasks([initTask]);
      } finally {
        setIsFetching(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived lists ──
  const drivers = useMemo(
    () => allPersonnel.filter((p) => p.role === "Driver"),
    [allPersonnel],
  );
  const helperPersonnel = useMemo(
    () => allPersonnel.filter((p) => p.role !== "Driver"),
    [allPersonnel],
  );

  const selectedVehicle = useMemo(
    () => vehicles.find((v) => v.id === form.vehicleId) || null,
    [vehicles, form.vehicleId],
  );
  const selectedDrivers = useMemo(
    () => allPersonnel.filter((p) => form.driverIds.includes(p.id)),
    [allPersonnel, form.driverIds],
  );
  const selectedPersonnel = useMemo(
    () => allPersonnel.filter((p) => form.personnelIds.includes(p.id)),
    [allPersonnel, form.personnelIds],
  );

  // ── DO/Task derived ──
  const selectedTasks = useMemo(
    () => availableTasks.filter((t) => selectedTaskIds.includes(t.uniqueId)),
    [availableTasks, selectedTaskIds],
  );
  const totalValue = selectedTasks.reduce(
    (a, b) => a + (b.totalAmount || 0),
    0,
  );

  const locationOptions = useMemo(() => {
    const locs = [
      ...new Set(availableTasks.map((t) => t.locationName).filter(Boolean)),
    ].sort();
    return ["All", ...locs];
  }, [availableTasks]);

  const filteredTasks = useMemo(() => {
    let list = availableTasks;
    if (locationFilter !== "All")
      list = list.filter((t) => t.locationName === locationFilter);
    if (taskSearch.trim()) {
      const q = taskSearch.toLowerCase();
      list = list.filter(
        (t) =>
          (t.documentNumber || "").toLowerCase().includes(q) ||
          (t.customerName || "").toLowerCase().includes(q) ||
          (t.salesOrderNumber || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [availableTasks, taskSearch, locationFilter]);

  const toggleTask = (id) =>
    setSelectedTaskIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const allVisibleSelected =
    filteredTasks.length > 0 &&
    filteredTasks.every((t) => selectedTaskIds.includes(t.uniqueId));

  const toggleAll = () => {
    if (allVisibleSelected) {
      const vis = new Set(filteredTasks.map((t) => t.uniqueId));
      setSelectedTaskIds((prev) => prev.filter((id) => !vis.has(id)));
    } else {
      const toAdd = filteredTasks
        .map((t) => t.uniqueId)
        .filter((id) => !selectedTaskIds.includes(id));
      setSelectedTaskIds((prev) => [...prev, ...toAdd]);
    }
  };

  const [doItems, setDOItems] = useState([])

  // ── Validation ──
  const canGoStep2 = selectedTaskIds.length > 0;
  const canGoStep3 = form.vehicleId !== null && form.driverIds.length > 0 && selectedTaskIds.length > 0 && form.shippingLocation && tripTicketNumberSeries;

  // ── Submit ──
  const handleSubmit = async () => {
    if (!canGoStep2 || !canGoStep3) return;
    setIsLoading(true);
    try {
      // Maps to CreateShipmentDto exactly
      const payload = {
        vehicleId: form.vehicleId,
        shippingLocation: form.shippingLocation?.trim() || null,
        driverIds: form.driverIds,
        personnelIds: form.personnelIds,
        plannedDate: new Date(form.plannedDate).toISOString(),
        notes: form.notes,
        createdByUserId: parseInt(currentUserId),
        deliveryOrderIds: selectedTaskIds
          .filter((id) => id.startsWith("DO-"))
          .map((id) => parseInt(id.replace("DO-", ""))),
        tradeReturnIds: selectedTaskIds
          .filter((id) => id.startsWith("TR-"))
          .map((id) => parseInt(id.replace("TR-", ""))),
        shipmentDeliveryOrderItems: form.selectedItems?.map((item) => ({
          itemId: item.id,
          deliveryOrderId: item.deliveryOrderMainId,
          status: "Pending",
        })),
        tripTicketNumber: String(tripTicketNumberSeries?.nextNumber ?? "").padStart( tripTicketNumberSeries?.numberLength ?? 1, "0" ),
        tripTicketNumberId: tripTicketNumberSeries?.id ?? null
      };

      const res = await axios.post(`${domain}/api/Shipments`, payload);

      const output = res.data?.shipment ?? null;

      if(output?.status === "Completed") {
        await handleThrowToAccounting(output)
      }

      toast.success(res.data?.message || "Shipment created successfully!");
      onSuccess?.();
      onClose();
      setTripTicketNumberSeries(null)
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create shipment.");
      console.error('There was a problem adding data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchById = async (_data) => {
    try {
      const res = await axios.get(`${domain}/api/Shipments/id/${_data.id}`);

      return res.data
    } catch (err) {
      console.error('There was a problem adding data:', err);
      return null
    }
  }

  const handleThrowToAccounting = async (_data) => {
    if (!_data) return;

    const fetchedData = await fetchById(_data)

    const dataToPost = {
      date: fetchedData?.plannedDate || null,
      shipmentNumber: fetchedData?.shipmentNumber || "",
      driver: fetchedData?.drivers?.[0]?.fullName || "",
      plateNo: fetchedData?.plateNumber || "",
      timeDeparture: fetchedData?.startedAt || null,
      shipmentDetails: fetchedData?.stops
        ?.flatMap(stop =>
          (stop.deliveryOrders || [])
            .filter(item => item.itemStatus === "Completed")
            .map(item => ({
              deliveryOrderHeaderId: item.documentNumber ?? ""
            }))
        ) || []
    };

    try {
      await axios.post(
        `${domainAccounting}/api/Shipment`, dataToPost ,
      );
      
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  // ─────────────────────────────────────────────────────────
  // STEP 1 — SELECT TASKS
  // ─────────────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="flex flex-col h-full min-h-0">
      {selectedTaskIds.length > 0 && (
        <div className="shrink-0 px-6 py-3 bg-indigo-50 border-b border-indigo-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold text-indigo-700 uppercase tracking-wide">
              {selectedTaskIds.length} Task
              {selectedTaskIds.length !== 1 ? "s" : ""} selected ·{" "}
              {formatCurrency(totalValue)}
            </span>
            <button
              onClick={() => setSelectedTaskIds([])}
              className="text-xs text-red-500 hover:text-red-700 font-semibold transition"
            >
              Clear all
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {selectedTasks.map((t) => (
              <SelectedTaskPill
                key={t.uniqueId}
                taskItem={t}
                onRemove={toggleTask}
                isPreSelected={initTask?.uniqueId === t.uniqueId}
              />
            ))}
          </div>
        </div>
      )}

      <div className="shrink-0 px-6 py-3 border-b border-gray-100 bg-white flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search DO/TR #, SO #, or customer..."
            value={taskSearch}
            onChange={(e) => setTaskSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
          />
        </div>
        <div className="relative min-w-[180px]">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="w-full pl-9 pr-8 py-2 border border-gray-200 rounded-xl text-sm bg-white outline-none focus:ring-2 focus:ring-indigo-200 appearance-none"
          >
            {locationOptions.map((l) => (
              <option key={l} value={l}>
                {l === "All" ? "All Locations" : l}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {isFetching ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            <p className="text-xs text-gray-400">Loading orders & returns…</p>
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
              <Package size={22} className="text-gray-400" />
            </div>
            <p className="text-gray-600 font-semibold text-sm">
              No available Tasks
            </p>
            <p className="text-gray-400 text-xs mt-1">
              {taskSearch
                ? "Try a different search term."
                : "All orders/returns are either dispatched or already in a shipment."}
            </p>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-3 w-10">
                  <div
                    onClick={toggleAll}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-all ${
                      allVisibleSelected
                        ? "bg-indigo-600 border-indigo-600"
                        : "border-gray-300 hover:border-indigo-400"
                    }`}
                  >
                    {allVisibleSelected && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path
                          d="M1 4L3.5 6.5L9 1"
                          stroke="white"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Order / Return #
                </th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">
                  Customer · SO
                </th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide text-right">
                  Amount
                </th>
                <th className="px-4 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map((task) => (
                <TaskRow
                  key={task.uniqueId}
                  task={task}
                  isSelected={selectedTaskIds.includes(task.uniqueId)}
                  onToggle={toggleTask}
                  isPreSelected={initTask?.uniqueId === task.uniqueId}
                />
              ))}
            </tbody>
          </table>


        )}
      </div>

      {!isFetching && filteredTasks.length > 0 && (
        <div className="shrink-0 px-6 py-2 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-400">
            Showing {filteredTasks.length} of {availableTasks.length} available
            tasks
            {locationFilter !== "All" && ` · filtered by ${locationFilter}`}
          </p>
        </div>
      )}
    </div>
  );

  // ─────────────────────────────────────────────────────────
  // STEP 2 — SHIPMENT INFO
  // ─────────────────────────────────────────────────────────
  const fetchDOItems = async (_deliveryOrders) => {
    try {
      const doIds = _deliveryOrders.map((d) => d.id);

      const res = await axios.post(`${domain}/api/DeliveryOrderMain/items`, {doIds: doIds});
      return res.data
    } catch (error) {
      console.error('There was a problem adding data:', error);
      return []
    }
  };

  useEffect(() => {
    const generateDOItems = async () => {
      setIsLoading(true)

      const _doItems = await fetchDOItems(selectedTasks)
      const unshippedItems = _doItems?.filter(i => i.shipmentStatus === "Pending" || !i.shipmentStatus)
      
      setDOItems(unshippedItems)
      setForm((prev) => ({
        ...prev,
        selectedItems: unshippedItems,
      }));

      setIsLoading(false)
    }

    if(selectedTasks && selectedTasks.length > 0){
      generateDOItems();
    }
  }, [selectedTasks])

  // Group doItems by their DO reference (adjust 'doNumber' to your actual field name)
  const groupedDOs = doItems.reduce((acc, item) => {
    const key = item.deliveryOrderNumber || "Unknown DO"; 
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});

  // Toggle handler
  const toggleItemSelection = (item, doNumber) => {
    const isSelected = form?.selectedItems?.some((i) => i.id === item.id);

    if (isSelected) {
      const selectedInThisGroup = form?.selectedItems?.filter((selected) =>
        groupedDOs[doNumber].some((groupItem) => groupItem.id === selected.id)
      );

      if (selectedInThisGroup.length === 1) {
        const confirmRemoval = window.confirm(
          `No item will be selected in DO #: ${doNumber}, the system will remove the DO. Are you sure you want to proceed?`
        );

        if (confirmRemoval) {
          // 1. Remove the Task ID from the source state
          // Assuming 'deliveryOrderMainId' is what matches the 'uniqueId' in selectedTaskIds
          setSelectedTaskIds((prevIds) => 
            prevIds.filter((id) => id !== `DO-${item.deliveryOrderMainId}`)
          );

          // 2. Remove the item from selection
          setForm((prev) => ({
            ...prev,
            selectedItems: prev?.selectedItems?.filter((i) => i.id !== item.id),
          }));
          
          // Note: Because selectedTaskIds changed, your useEffect 
          // will now trigger to refresh the DO items list.
          return; 
        } else {
          return; // User cancelled
        }
      }
      // Standard removal if it's NOT the last item
      setForm((prev) => ({
        ...prev,
        selectedItems: prev?.selectedItems?.filter((i) => i.id !== item.id),
      }));
    } else {
      // Add item back
      setForm((prev) => ({
        ...prev,
        selectedItems: [...prev.selectedItems, item],
      }));
    }
  };

  const renderStep2 = () => {
    const totalShipmentValue = form?.selectedItems?.reduce(
      (sum, item) => sum + (item.lineTotal || 0),
      0
    );
    
    return (
      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
          <p className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-2">
            Tasks in this shipment ({selectedTaskIds.length})
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {selectedTasks.map((t) => (
              <div
                key={t.uniqueId}
                className="flex items-center justify-between bg-white border border-indigo-100 rounded-lg px-3 py-2"
              >
                <div>
                  <p className="text-xs font-bold text-gray-800 flex items-center gap-1.5">
                    {t.taskType === "DO" ? (
                      <Package size={11} className="text-blue-500" />
                    ) : (
                      <RotateCcw size={11} className="text-green-500" />
                    )}
                    {t.documentNumber}
                  </p>
                  <p className="text-[10px] text-gray-400 truncate">
                    {t.customerName} · {t.locationName || "—"}
                  </p>
                </div>
                <span className="text-xs font-bold text-indigo-600 shrink-0 ml-2">
                  {formatCurrency(t.totalAmount)}
                </span>
              </div>
            ))}
          </div>

          {selectedTaskIds.length > 0 ? 
            <div className="bg-white p-4 rounded-lg shadow border mt-4">
              <h3 className="text-xs font-bold text-indigo-600 uppercase tracking-wide mb-2">SELECT ITEMS TO SHIP</h3>
              
              {Object.keys(groupedDOs).map((doNumber) => (
                <div key={doNumber} className="mb-6 last:mb-0 border rounded-md overflow-hidden">
                  {/* DO Header */}
                  <div className="bg-gray-100 px-4 py-2 font-semibold border-b flex justify-between items-center">
                    <span className="text-xs font-bold text-gray-800 flex items-center gap-1.5">DO #: {doNumber}</span>
                    <span className="text-sm font-normal text-gray-500">
                      {groupedDOs[doNumber].length} items
                    </span>
                  </div>

                  {/* Items List */}
                  <div className="divide-y">
                    {groupedDOs[doNumber].map((item) => {
                      const isChecked = form?.selectedItems?.some((i) => i.id === item.id);
                      return (
                        <div 
                          key={item.id} 
                          className={`flex items-center p-3 hover:bg-gray-50 transition-colors ${!isChecked ? 'opacity-60' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`item-${item.id}`}
                            className="w-4 h-4 cursor-pointer accent-blue-600"
                            checked={isChecked}
                            onChange={() => toggleItemSelection(item, doNumber)} 
                          />
                          <label 
                            htmlFor={`item-${item.id}`} 
                            className="ml-3 flex-grow cursor-pointer "
                          >
                            <div className="text-xs font-bold text-gray-800 flex items-center gap-1.5">{`${item.productName} ${item.brandName ? `(${item.brandName})` : ""}`}</div>
                            <div className="text-sm text-gray-500">
                              Qty: {item.quantity}
                            </div>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              {/* Summary footer */}
              <div className="mt-4 pt-4 border-t text-right font-semibold">
                Total Selected: {form?.selectedItems?.length} / {doItems.length}
              </div>
            </div>
            :
            <div className="flex flex-col items-center justify-center p-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50 mt-4">
              <div className="w-12 h-12 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <p className="text-sm font-medium text-gray-900">No delivery orders selected</p>
              <p className="text-xs text-gray-500 mt-1 max-w-[200px] text-center">
                Please go back to <span className="font-bold text-indigo-600">Step 1</span> to select the Delivery Orders you wish to ship.
              </p>
            </div>
          }

          <div className="mt-3 pt-3 border-t border-indigo-200 flex justify-between">
            <span className="text-xs font-bold text-indigo-700">
              Total Shipment Value
            </span>
            <span className="text-sm font-bold text-indigo-800 hidden">
              {formatCurrency(totalValue)}
            </span>
            <span className="text-sm font-bold text-indigo-800">
              {formatCurrency(totalShipmentValue)}
            </span>
          </div>
        </div>

        <div className="flex flex-row gap-2">
          <div className="w-full">
            <label htmlFor="terms" className={"block text-sm font-semibold text-gray-700 mb-1"}>
              Trip Ticket Number Series
            </label>

            <TripTicketNumberSeriesSelect data={tripTicketNumberSeries} setData={setTripTicketNumberSeries} isEditable={true}/>
          </div>
          
          <div className="w-full">
            <label
              htmlFor="ticketNumber"
              className="mb-1 block text-sm font-semibold text-gray-700"
            >
              Ticket Number
            </label>

            <input
              id="ticketNumber"
              className="block w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200"
              value={String(tripTicketNumberSeries?.nextNumber ?? "").padStart(
                tripTicketNumberSeries?.numberLength ?? 1,
                "0"
              )}
              readOnly
            />

            <p className="mt-1.5 flex items-start gap-1.5 text-xs leading-5 text-gray-500">
              <Info size={14} className="mt-0.5 shrink-0 text-indigo-500" />
              <span>
                This ticket number is generated automatically. To change the ticket
                number, manage it in the <strong>Trip Ticket Number Series</strong> module.
              </span>
            </p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
          <h3 className="text-sm font-bold text-gray-700 flex items-center gap-2">
            <Truck size={14} className="text-indigo-500" /> Logistics Information
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>
                <Car size={11} className="inline mr-1" />
                Vehicle *
              </label>
              <button
                type="button"
                onClick={() => setVehicleModalOpen(true)}
                className={`${inputCls} text-left flex items-center justify-between cursor-pointer`}
              >
                <span
                  className={selectedVehicle ? "text-gray-800" : "text-gray-400"}
                >
                  {selectedVehicle
                    ? selectedVehicle.plateNumber
                    : "Click to select vehicle…"}
                </span>
                <ChevronDown size={15} className="text-gray-400 shrink-0 ml-2" />
              </button>
              <p className="text-[10px] text-gray-400 mt-1">
                {selectedVehicle?.vehicleName ||
                  selectedVehicle?.vehicleType ||
                  "Select truck assignment"}
              </p>
            </div>

            <div>
              <label className={labelCls}>
                <User size={11} className="inline mr-1" />
                Driver(s) *
              </label>
              <button
                type="button"
                onClick={() => setDriverModalOpen(true)}
                className={`${inputCls} text-left flex items-center justify-between cursor-pointer`}
              >
                <span
                  className={
                    form.driverIds.length > 0
                      ? "text-gray-800 truncate"
                      : "text-gray-400"
                  }
                >
                  {form.driverIds.length > 0
                    ? selectedDrivers.map(getFullName).join(", ")
                    : "Click to select driver(s)…"}
                </span>
                <ChevronDown size={15} className="text-gray-400 shrink-0 ml-2" />
              </button>
            </div>

            <div>
              <label className={labelCls}>
                <Users size={11} className="inline mr-1" />
                Personnel
              </label>
              <button
                type="button"
                onClick={() => setPersonnelModalOpen(true)}
                className={`${inputCls} text-left flex items-center justify-between cursor-pointer`}
              >
                <span
                  className={
                    form.personnelIds.length > 0
                      ? "text-gray-800 truncate"
                      : "text-gray-400"
                  }
                >
                  {form.personnelIds.length > 0
                    ? selectedPersonnel.map(getFullName).join(", ")
                    : "Click to select helpers / loaders…"}
                </span>
                <ChevronDown size={15} className="text-gray-400 shrink-0 ml-2" />
              </button>
              <p className="text-[10px] text-gray-400 mt-1">
                Helpers / loaders (optional)
              </p>
            </div>

            <div>
              <label className={labelCls}>
                <Calendar size={11} className="inline mr-1" />
                Planned Departure *
              </label>
              <input
                type="datetime-local"
                value={form.plannedDate}
                onChange={(e) =>
                  setForm((f) => ({ ...f, plannedDate: e.target.value }))
                }
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>
                <ClipboardList size={11} className="inline mr-1" />
                Sales Orders by Salesman
              </label>
              <div className="space-y-2 mt-1">
                {/* Logic to filter by Salesman and collect Sales Order Numbers */}
                {Object.values(
                  selectedTasks.reduce((acc, task) => {
                    // We use the salesman property or the name from the first SO entry
                    const sellerName = task.salesman || (task.salesOrderNumbers?.[0]?.salesmanName) || "No Salesman Assigned";
                    
                    const key = sellerName;
                    
                    if (!acc[key]) {
                      acc[key] = {
                        name: sellerName,
                        customer: task.customerName,
                        sos: [] // Array to store unique Sales Order Numbers
                      };
                    }

                    // Iterate through the salesOrderNumbers array in the task
                    task.salesOrderNumbers?.forEach((so) => {
                      if (!acc[key].sos.includes(so.salesOrderNumber)) {
                        acc[key].sos.push(so.salesOrderNumber);
                      }
                    });

                    return acc;
                  }, {})
                ).map((seller, index) => (
                  <div key={index} className="flex flex-col bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                          Salesman
                        </span>
                        <span className="text-sm font-bold text-slate-800">
                          {seller.name}
                        </span>
                      </div>
                      
                      {/* Display all associated SO numbers as badges */}
                      <div className="flex gap-1 flex-wrap justify-end max-w-[50%]">
                        {seller.sos.map((soNumber, idx) => (
                          <span key={idx} className="text-[9px] font-mono bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded border border-blue-200">
                            {soNumber}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Retention of Customer Name */}
                    <div className="mt-1 pt-1 border-t border-slate-200/60">
                      <span className="text-[11px] text-slate-500">
                        <span className="font-medium">Client:</span> {seller.customer}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>
                <Clock size={11} className="inline mr-1" />
                Terms
              </label>
              <div className="space-y-2 mt-1">
                {Object.values(
                  selectedTasks.reduce((acc, task) => {
                    // Group by TermsDescription (e.g., "COD", "30 Days", "7 Days")
                    const key = task.termsDescription || "No Terms Set";
                    
                    if (!acc[key]) {
                      acc[key] = {
                        description: key,
                        // Use a Set for customers to avoid repeating the same name if they have multiple DOs
                        customers: new Set([task.customerName]),
                        docs: [task.documentNumber]
                      };
                    } else {
                      acc[key].docs.push(task.documentNumber);
                      acc[key].customers.add(task.customerName);
                    }
                    return acc;
                  }, {})
                ).map((term, index) => (
                  <div key={index} className="flex flex-col bg-slate-50 p-2 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between">
                      {/* Display the Terms Description */}
                      <span className="text-sm font-bold text-slate-800">
                        {term.description}
                      </span>
                      
                      {/* Display all DO numbers associated with this term */}
                      <div className="flex gap-1 flex-wrap justify-end">
                        {term.docs.map((doc, idx) => (
                          <span key={idx} className="text-[9px] font-mono bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                    
                    {/* Display the unique customers under this term */}
                    <span className="text-[11px] text-slate-500 italic">
                      Clients: {Array.from(term.customers).join(", ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="col-span-1 sm:col-span-2">
              <label className={labelCls}>
                <MapPin size={11} className="inline mr-1" />
                Shipping Location *
              </label>
              <input
                type="text"
                placeholder="e.g. Main Plant, Batangas Warehouse"
                value={form.shippingLocation}
                onChange={(e) =>
                  setForm((f) => ({ ...f, shippingLocation: e.target.value }))
                }
                className={inputCls}
              />
              <p className="text-[10px] text-gray-400 mt-1">
                Starting point / Origin (optional)
              </p>
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea
              rows={2}
              placeholder="e.g. Call customers 30 mins before arrival."
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              className={`${inputCls} resize-none`}
            />
          </div>

          {(!form.vehicleId || form.driverIds.length === 0) && (
            <p className="text-xs text-amber-600 flex items-center gap-1.5">
              <Info size={12} /> Vehicle and at least one Driver are required to
              continue.
            </p>
          )}
        </div>
      </div>
  )};

  // ─────────────────────────────────────────────────────────
  // STEP 3 — REVIEW
  // ─────────────────────────────────────────────────────────
  const renderStep3 = () => (
    <div className="flex-1 overflow-y-auto p-6 space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex gap-3">
        <Info size={14} className="text-blue-500 shrink-0 mt-0.5" />
        <div className="text-xs text-blue-800">
          <p className="font-bold mb-0.5">Review before creating</p>
          <p>
            Once created, the shipment starts as <strong>Pending</strong>. Click{" "}
            <strong>"Start Shipment"</strong> when the truck actually departs.
            At each stop, mark each task as <strong>Completed</strong> or{" "}
            <strong>Failed</strong>.
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">
          Logistics Details
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
              <Car size={10} /> Vehicle
            </p>
            <p className="font-bold text-gray-800">
              {selectedVehicle?.vehicleName || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
              <User size={10} /> Driver(s)
            </p>
            <p className="font-bold text-gray-800">
              {selectedDrivers.length > 0
                ? selectedDrivers.map(getFullName).join(", ")
                : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
              <Users size={10} /> Personnel
            </p>
            <p className="font-bold text-gray-800">
              {selectedPersonnel.length > 0
                ? selectedPersonnel.map(getFullName).join(", ")
                : "—"}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
              <Calendar size={10} /> Planned Date
            </p>
            <p className="font-bold text-gray-800">
              {formatDate(form.plannedDate)}
            </p>
          </div>
          <div className="col-span-2">
            <p className="text-xs text-gray-400 mb-0.5 flex items-center gap-1">
              <MapPin size={10} /> Shipping Location (Origin)
            </p>
            <p className="font-bold text-gray-800">
              {form.shippingLocation || "—"}
            </p>
          </div>
          {form.notes && (
            <div className="col-span-3">
              <p className="text-xs text-gray-400 mb-0.5">Notes</p>
              <p className="text-gray-700 text-xs">{form.notes}</p>
            </div>
          )}
        </div>
      </div>

      {(() => {
        /* const byLocation = selectedTasks.reduce((acc, d) => {
          const loc = d.locationName || "Unknown";
          if (!acc[loc]) acc[loc] = [];
          acc[loc].push(d);
          return acc;
        }, {}); */
        const expandedTasks = selectedTasks.flatMap(d =>
          d.salesOrderNumbers.map(so => ({
            ...d,
            locationName: so.locationName,
            salesOrderNumber: so.salesOrderNumber
          }))
        );

        const byLocation = expandedTasks.reduce((acc, d) => {
          const loc = d.locationName || "Unknown";

          if (!acc[loc]) acc[loc] = [];
          acc[loc].push(d);

          return acc;
        }, {});
        const locations = Object.entries(byLocation);

        const totalShipmentValue = form?.selectedItems?.reduce(
          (sum, item) => sum + (item.lineTotal || 0),
          0
        );

        return (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <p className="text-xs font-bold text-gray-700 flex items-center gap-2">
                <Package size={13} className="text-indigo-500" />
                Delivery & Return Tasks ({selectedTasks.length}) ·{" "}
                {/* {locations.length} stop
                {locations.length !== 1 ? "s" : ""} */}
              </p>
              <span className="text-xs font-bold text-indigo-600 hidden">
                {formatCurrency(totalShipmentValue)}
              </span>
              <div className="flex items-center gap-1.5">
                <MapPin size={12} className="text-indigo-400" />
                <span className="text-xs font-bold text-gray-700">
                  {form.shippingLocation}
                </span>
              </div>
            </div>

            <div className="hidden">
              {locations.map(([loc, tasks], idx) => (
                <div key={loc}>
                  <div className="flex items-center gap-3 px-5 py-2.5 bg-gray-50 border-b border-gray-100">
                    <div className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold hidden items-center justify-center shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin size={12} className="text-indigo-400" />
                      <span className="text-xs font-bold text-gray-700">
                        {loc}
                      </span>
                    </div>
                    <span className="text-[10px] text-gray-400 ml-auto">
                      {tasks.length} Task{tasks.length !== 1 ? "s" : ""} ·{" "}
                      {formatCurrency(
                        tasks.reduce((a, b) => a + (b.totalAmount || 0), 0),
                      )}
                    </span>
                  </div>
                  <table className="w-full text-xs text-left">
                    <tbody className="divide-y divide-gray-50">
                      {tasks.map((t) => (
                        <tr key={t.uniqueId} className="hover:bg-gray-50">
                          <td className="px-5 py-2.5 pl-14 font-bold text-gray-800 flex items-center gap-2">
                            {t.taskType === "DO" ? (
                              <Package size={11} className="text-blue-500" />
                            ) : (
                              <RotateCcw size={11} className="text-green-500" />
                            )}
                            {t.documentNumber}
                          </td>
                          <td className="px-4 py-2.5 text-gray-600">
                            {t.customerName}
                          </td>
                          <td className="px-4 py-2.5 text-right font-bold text-gray-800">
                            {formatCurrency(t.totalAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>

            <table className="w-full text-xs text-left">
              <tbody className="divide-y divide-gray-100">
                {selectedTasks.map((t) => {
                  // Filter items belonging to this specific DO/Task
                  const itemsForThisTask = form?.selectedItems?.filter(
                    (i) => i.deliveryOrderMainId === t.id
                  );

                  return (
                    <React.Fragment key={t.uniqueId}>
                      {/* MAIN DO ROW */}
                      <tr className="bg-white hover:bg-gray-50 transition-colors">
                        <td className="px-5 py-3 pl-14 font-bold text-gray-800 flex items-center gap-2">
                          {t.taskType === "DO" ? (
                            <Package size={12} className="text-blue-500" />
                          ) : (
                            <RotateCcw size={12} className="text-green-500" />
                          )}
                          <span className="tracking-tight">{t.documentNumber}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {t.customerName}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-800">
                          {formatCurrency(t.totalAmount)}
                        </td>
                      </tr>

                      {/* ITEMS DETAIL ROW */}
                      {itemsForThisTask?.length > 0 && (
                        <tr className="bg-white">
                          <td colSpan={3} className="px-5 pb-4 pl-20">
                            <div className="bg-gray-50 rounded-lg border border-gray-100 overflow-hidden">
                              <table className="w-full text-[10px] text-gray-500">
                                <thead>
                                  <tr className="bg-gray-100/50 text-gray-400 uppercase tracking-widest border-b border-gray-100">
                                    <th className="px-3 py-1.5 font-semibold">Product Description</th>
                                    <th className="px-3 py-1.5 text-right font-semibold">Total</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                  {itemsForThisTask.map((item) => (
                                    <tr key={item.id}>
                                      <td className="px-3 py-1.5 font-medium text-gray-700">
                                        {item.productName}
                                      </td>
                                      <td className="px-3 py-1.5 text-right">
                                        {formatCurrency(item.lineTotal)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="px-5 py-2.5 bg-gray-50 border-t border-gray-200 flex justify-between">
              <span className="text-xs font-bold text-gray-600">Total</span>
              <span className="text-xs font-bold text-indigo-700">
                {formatCurrency(totalShipmentValue)}
              </span>
            </div>
          </div>
        );
      })()}
    </div>
  );

  // ─────────────────────────────────────────────────────────
  // LAYOUT
  // ─────────────────────────────────────────────────────────
  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/70 p-4 backdrop-blur-sm">
        <div className="relative flex h-full max-h-[95vh] w-full max-w-4xl flex-col rounded-2xl bg-white shadow-2xl overflow-hidden">
          <div className="shrink-0 border-b bg-white px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-600 rounded-xl text-white">
                  <Truck size={18} />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    New Shipment
                  </h2>
                  <p className="text-xs text-gray-500">
                    Select tasks (orders/returns) · assign logistics · review
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            <StepIndicator step={step} />
          </div>

          {isLoading && (
            <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/70">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
            </div>
          )}

          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>

          <div className="shrink-0 border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
            <div className="text-xs text-gray-500">
              {step === 1 &&
                (selectedTaskIds.length > 0 ? (
                  <span className="font-semibold text-indigo-600">
                    {selectedTaskIds.length} Task
                    {selectedTaskIds.length !== 1 ? "s" : ""} selected ·{" "}
                    {formatCurrency(totalValue)}
                  </span>
                ) : (
                  <span className="text-gray-400">
                    Select at least one task
                  </span>
                ))}
              {step === 2 && (
                <span className="text-gray-400">
                  Fill in vehicle and driver to continue
                </span>
              )}
              {step === 3 && (
                <span className="font-semibold text-green-600">
                  Ready to create · {selectedTasks.length} Task
                  {selectedTasks.length !== 1 ? "s" : ""} ·{" "}
                  {/* {formatCurrency(totalValue)} */}
                </span>
              )}
            </div>

            <div className="flex gap-3">
              {step > 1 && (
                <button
                  onClick={() => setStep((s) => s - 1)}
                  className="px-4 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                >
                  Back
                </button>
              )}
              {step < 3 ? (
                <button
                  onClick={() => setStep((s) => s + 1)}
                  disabled={step === 1 ? !canGoStep2 : !canGoStep3}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
                >
                  Next <ArrowRight size={15} />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition disabled:opacity-50 shadow-sm"
                >
                  {isLoading && (
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  )}
                  <Truck size={15} />
                  Create Shipment
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <VehicleSelectionModal
        isOpen={vehicleModalOpen}
        onClose={() => setVehicleModalOpen(false)}
        vehicles={vehicles}
        selectedId={form.vehicleId}
        onConfirm={(id) => setForm((f) => ({ ...f, vehicleId: id }))}
      />

      <PersonnelSelectionModal
        isOpen={driverModalOpen}
        onClose={() => setDriverModalOpen(false)}
        title="Select Drivers"
        icon={Truck}
        personnel={drivers}
        selectedIds={form.driverIds}
        onConfirm={(ids) => setForm((f) => ({ ...f, driverIds: ids }))}
        isOptional={false} // <--- Driver is required
      />

      <PersonnelSelectionModal
        isOpen={personnelModalOpen}
        onClose={() => setPersonnelModalOpen(false)}
        title="Select Personnel / Helpers"
        icon={Users}
        personnel={helperPersonnel}
        selectedIds={form.personnelIds}
        onConfirm={(ids) => setForm((f) => ({ ...f, personnelIds: ids }))}
        isOptional={true} // <--- Personnel is Optional!
      />
    </>
  );
};

export default CreateShipmentModal;
